import { useState } from 'react';
import { AppFab } from '@core/designsystem';
import { BottomBar } from './navigation/BottomBar';
import { CreateSheet, type CreateAction } from './navigation/CreateSheet';
import { NavigationStack } from './navigation/NavigationStack';
import { hidesBottomBar, hidesFab, parse, routes, type Screen } from './navigation/routes';
import { useNavigator } from './navigation/useNavigator';
import { Catalogue } from './Catalogue';
import { CasesRoute } from '@features/cases/CasesRoute';
import { CaseDetailRoute } from '@features/cases/CaseDetailRoute';
import { DocumentsRoute } from '@features/documents/DocumentsRoute';
import { QuickScanRoute } from '@features/documents/QuickScanRoute';
import { ViewerRoute } from '@features/documents/ViewerRoute';
import { TasksRoute } from '@features/tasks/TasksRoute';
import { TaskDetailRoute } from '@features/tasks/TaskDetailRoute';
import { CalendarRoute } from '@features/calendar/CalendarRoute';
import { EventDetailRoute } from '@features/calendar/EventDetailRoute';
import { ClientsRoute } from '@features/clients/ClientsRoute';
import { ClientDetailRoute } from '@features/clients/ClientDetailRoute';
import { SearchRoute } from '@features/search/SearchRoute';
import { SettingsRoute } from '@features/settings/SettingsRoute';
import './shell.css';

/**
 * Каркас приложения: маршруты, нижняя панель, «плюс» и переходы между экранами.
 *
 * Заглушек больше нет: последняя — «Быстрый скан» — жила здесь дольше всех и досталась
 * первому же тестировщику как «выдало какую-то ошибку». Экран, обещающий шаг плана,
 * для юриста неотличим от сломанного.
 */
export function AppShell() {
  const nav = useNavigator();
  const [creating, setCreating] = useState(false);
  /**
   * Что именно создаётся: лист «плюса» выбирает, а диалог живёт на своём экране.
   * Так каркас не знает про поля дела, а экран — про кнопку.
   */
  const [creatingEntity, setCreatingEntity] = useState<CreateAction | null>(null);
  /**
   * Кадр быстрого скана. Живёт в каркасе, а не в адресе: файл через адрес не передать,
   * а снят он в «плюсе» — камеру Safari открывает только из самого нажатия.
   */
  const [scanned, setScanned] = useState<File | null>(null);

  const screen = parse(nav.path);
  const searching = screen.name === 'search';

  const create = (action: CreateAction) => {
    // Раздел, где запись появится. Дальше экран сам решит, показывать ли диалог:
    // у дел он уже есть, остальные придут своими шагами.
    const target: Record<CreateAction, string> = {
      case: routes.cases,
      client: routes.clients,
      task: routes.tasks,
      event: routes.calendar,
    };

    setCreatingEntity(action);
    if (nav.path !== target[action]) nav.push(target[action]);
  };

  const scan = (photos: File[]) => {
    const photo = photos[0];
    if (!photo) return;

    setScanned(photo);
    if (nav.path !== routes.quickScan) nav.push(routes.quickScan);
  };

  const bottomBar = !hidesBottomBar(screen);

  return (
    // Когда панель есть, «плюс» поднимается над ней: иначе он накрывает собой
    // пункт «Поиск» — поймано прогоном 04.09.2026, до этого кнопка просто ела касание.
    <div className={`shell ${bottomBar ? 'shell--with-bar' : ''}`}>
      <NavigationStack
        path={nav.path}
        direction={nav.direction}
        canGoBack={nav.canGoBack}
        onBack={nav.back}
      >
        {renderScreen(screen, nav, creatingEntity, setCreatingEntity, {
          photo: scanned,
          onShot: scan,
          onDone: () => setScanned(null),
        })}
      </NavigationStack>

      {hidesFab(screen) ? null : (
        <AppFab icon="add" label="Создать" onClick={() => setCreating(true)} />
      )}

      {bottomBar ? (
        <BottomBar
          tab={nav.tab}
          searching={searching}
          onSelect={nav.switchTab}
          onSearch={() => nav.push(routes.search)}
        />
      ) : null}

      {creating ? (
        <CreateSheet onDismiss={() => setCreating(false)} onSelect={create} onScan={scan} />
      ) : null}
    </div>
  );
}

function renderScreen(
  screen: Screen,
  nav: ReturnType<typeof useNavigator>,
  creatingEntity: CreateAction | null,
  setCreatingEntity: (action: CreateAction | null) => void,
  quickScan: { photo: File | null; onShot: (photos: File[]) => void; onDone: () => void },
) {
  const back = nav.back;

  switch (screen.name) {
    case 'cases':
      return (
        <CasesRoute
          onOpenCase={(id) => nav.push(routes.caseDetail(id))}
          onScan={(id) => nav.push(routes.caseDocuments(id))}
          onOpenSettings={() => nav.push(routes.settings)}
          creating={creatingEntity === 'case'}
          onCreatingChange={(open) => setCreatingEntity(open ? 'case' : null)}
        />
      );
    case 'tasks':
      return (
        <TasksRoute
          onOpenTask={(id) => nav.push(routes.task(id))}
          onOpenCase={(id) => nav.push(routes.caseDetail(id))}
          creating={creatingEntity === 'task'}
          onCreatingChange={(open) => setCreatingEntity(open ? 'task' : null)}
        />
      );
    case 'clients':
      return (
        <ClientsRoute
          onOpenClient={(id) => nav.push(routes.client(id))}
          creating={creatingEntity === 'client'}
          onCreatingChange={(open) => setCreatingEntity(open ? 'client' : null)}
        />
      );
    case 'calendar':
      return (
        <CalendarRoute
          onOpenEvent={(id) => nav.push(routes.event(id))}
          onOpenClient={(id) => nav.push(routes.client(id))}
          creating={creatingEntity === 'event'}
          onCreatingChange={(open) => setCreatingEntity(open ? 'event' : null)}
        />
      );
    case 'search':
      return (
        <SearchRoute
          onBack={back}
          onOpen={(item) => {
            // Куда ведёт результат, знает каркас: сам поиск про чужие экраны не знает.
            if (item.category === 'cases') nav.push(routes.caseDetail(item.id));
            if (item.category === 'tasks') nav.push(routes.task(item.id));
            if (item.category === 'documents') nav.push(routes.document(item.id));
            if (item.category === 'clients') nav.push(routes.client(item.id));
          }}
        />
      );
    case 'settings':
      return (
        <SettingsRoute onBack={back} onOpenCase={(id) => nav.push(routes.caseDetail(id))} />
      );
    case 'catalogue':
      return <Catalogue onBack={back} />;
    case 'quickScan':
      return (
        <QuickScanRoute
          photo={quickScan.photo}
          onShot={quickScan.onShot}
          onBack={() => {
            quickScan.onDone();
            back();
          }}
          onSaved={(caseId) => {
            // Сразу в дело, куда положили: снимали не в пустоту, и результат надо
            // показать. Назад из дела ведёт к списку, а не обратно в съёмку.
            quickScan.onDone();
            nav.replace(routes.caseDocuments(caseId));
          }}
        />
      );
    case 'caseDetail':
      return (
        <CaseDetailRoute
          caseId={screen.caseId}
          onBack={back}
          onOpenDocuments={() => nav.push(routes.caseDocuments(screen.caseId))}
          onOpenDocument={(id) => nav.push(routes.document(id))}
          onOpenClient={(id) => nav.push(routes.client(id))}
          onOpenEvent={(id) => nav.push(routes.event(id))}
        />
      );
    case 'caseDocuments':
      return (
        <DocumentsRoute
          caseId={screen.caseId}
          onBack={back}
          onOpenDocument={(id) => nav.push(routes.document(id))}
        />
      );
    case 'client':
      return (
        <ClientDetailRoute
          clientId={screen.clientId}
          onBack={back}
          onOpenCase={(id) => nav.push(routes.caseDetail(id))}
          onOpenDocument={(id) => nav.push(routes.document(id))}
        />
      );
    case 'task':
      return (
        <TaskDetailRoute
          taskId={screen.taskId}
          onBack={back}
          onOpenCase={(id) => nav.push(routes.caseDetail(id))}
          onOpenClient={(id) => nav.push(routes.client(id))}
        />
      );
    case 'event':
      return (
        <EventDetailRoute
          eventId={screen.eventId}
          onBack={back}
          onOpenCase={(id) => nav.push(routes.caseDetail(id))}
          onOpenClient={(id) => nav.push(routes.client(id))}
        />
      );
    case 'document':
      return <ViewerRoute documentId={screen.documentId} onBack={back} />;
  }
}
